import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { KeyValuePair, UploadedFileModel, UploadedFilesResponse } from '@/models/AdditionalDataTypes'
import { InterviewQuestionResponseModel } from '@/models/QuestionModels'
import { QuestionType } from '@/models/QuestionType';
import { Variant } from './../models/QuestionModels';
import { SidebarImages } from '@/models/SidebarImages';
import { QuestionaryService } from '@/services/questionary.service';
import { ThankYouPage } from '@/models/ThankYouPage';

export const useInterviewStore = defineStore('interviewStore', () => {
    const pdfPreviewIcon = '/images/pdf-ico.svg'
    const docFilePreview = '/images/document_ico.svg'
    const queryCategoryKey = 'CategoryId'

    const locallizations = ref<{ [key: string]: string }>({})
    const thankYouPage = ref<ThankYouPage>();
    const sidebarImages = ref<SidebarImages | undefined>(undefined)
    const isTerms = ref<boolean>()
    const userName = ref<string>('')
    const maxFileSize = ref<number>(0)
    // Kept alongside the byte value so copy can quote the configured limit instead of
    // hardcoding a number that drifts away from what is actually enforced.
    const maxFileSizeMb = ref<number>(0)

    const googleMapKey = ref<string>('')

    function getPreview(file: UploadedFileModel) {
        if (file.image.type === 'application/pdf') {
            return pdfPreviewIcon
        }else if (file.image.type === 'application/msword' || file.image.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            return docFilePreview
        }

        return URL.createObjectURL(file.image)
    }

    function isValidQuestion(dto: InterviewQuestionResponseModel): boolean {
        if (dto.Type && dto.Type.length > 0 && dto.Title && dto.Title.length > 0 && dto.Id && dto.Id.length > 0) {
            return true
        }
        return false
    }

    function mapQuestions(questions: InterviewQuestionResponseModel[]) {
        return questions
            .filter((q) => isValidQuestion(q))
            .map((q) => ({ key: q, value: '' }));
    }

    function getCategoryAnswer(val?: Variant): string {
        return `${val?.CategoryId};${val?.Label}`
    }

    function getUrlQuestionWithAnswer(questions: InterviewQuestionResponseModel[]): KeyValuePair<InterviewQuestionResponseModel, string> | undefined {
        // we could have only one category question prt questionaire
        const categoryQuestion = questions.find(q => q.Type === QuestionType.CategoryQuestion);
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const catId = categoryQuestion && urlParams && urlParams.get(queryCategoryKey) || '';
        const variant = categoryQuestion?.Variants?.find(v => v?.CategoryId !== undefined && v?.CategoryId === Number.parseInt(catId))
        const value = getCategoryAnswer(variant)

        if (!categoryQuestion || !urlParams) {
            return
        }

        return { key: categoryQuestion, value };
    }

    async function fetchQuestions() {
        const { SideBar, Localization, Questions, ThankYouPage, MaxFileSize, GoogleMapKey } = await QuestionaryService.fetchQuestions();

        locallizations.value = Localization
        sidebarImages.value = SideBar;
        thankYouPage.value = ThankYouPage;
        maxFileSizeMb.value = MaxFileSize
        maxFileSize.value = MaxFileSize * 1000 * 1000
        googleMapKey.value = GoogleMapKey

        questionsAnswer.value = mapQuestions(Questions || []);

        const urlQuestion = getUrlQuestionWithAnswer(Questions);

        if (urlQuestion) {
            const variants = urlQuestion.key.Variants?.find(v => `${v.CategoryId};${v.Label}` === urlQuestion.value);

            if (variants) {
                replaceVariants(urlQuestion.key, variants);
                // resetCategoryIndex(urlQuestion.key);

                urlQuestion.key.hideQuestion = true;
                questionsAnswer.value = questionsAnswer.value.map(i => i.key.Id === urlQuestion.key.Id ? urlQuestion : i)
            }
        }

        nextQuestion()
    }

    function getLocalization(key: string) {
        let res = ''

        try {
            res = locallizations.value[key]
        } catch (error) {
            console.warn(`No localization for ${key}`)
        }

        return res
    }

    async function submitMedia(files: UploadedFileModel[], onProgress?: (percent: number) => void): Promise<UploadedFilesResponse[]> {
        const res = await QuestionaryService.submitMedia(files, onProgress);
        return <UploadedFilesResponse[]>res
    }

    async function postQuestionaryAnswers() {
        try {
            const { AdditionalQuestions } = await QuestionaryService.postQuestionaryAnswers(questionsAnswer.value, isWithAdditionalQuestions.value);

            if (AdditionalQuestions && AdditionalQuestions.length > 0) {
                isWithAdditionalQuestions.value = true
                questionsAnswer.value = [...questionsAnswer.value,...mapQuestions(AdditionalQuestions)]
                processProgress()
            } else {
                isThanksPageShowing.value = true
            }

        } catch (error) {
            console.warn('TODO: manage postQuestionaryAnswers send error', error)
        }
    }

    function setUserName(name:string) {
        if (name?.length > 0) {
            userName.value = name
        }
    }

    function setCookie(name: string, value: boolean, days: number) {
        let expires = ""
        if (days) {
            const date = new Date()
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
            expires = "; expires=" + date.toUTCString()
        }

        document.cookie = name + "=" + (value || "") + expires + "; path=/"
        if (name === "termsOfUse")
        {
            isTerms.value = true
        }
    }

    function getCookie(name: string) {
        const nameEQ = name + "="
        const ca = document.cookie.split(';')
    
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i]
    
            while (c.charAt(0) == ' ') {
                c = c.substring(1, c.length)
            }
    
            if (c.indexOf(nameEQ) == 0) {
                return c.substring(nameEQ.length, c.length)
            }
        }
    
        return null
    }
    function eraseCookie(name: string) {
        document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        if (name === "termsOfUse")
        {
            isTerms.value = false
        }
    }
    
    //
    //#region Questionary progress
    const progress = ref<number>(0)
    const questionIndex = ref<number>(-1)
    const questionsAnswer = ref<Array<KeyValuePair<InterviewQuestionResponseModel, string>>>([])
    const questionsAnswerError = ref<Array<Object>>([])
    const currentQuestion = ref<KeyValuePair<InterviewQuestionResponseModel, string> | null>(null)
    const isWithAdditionalQuestions = ref<boolean>(false)
    const isThanksPageShowing = ref<boolean>(false)
    const isTermsShowing = ref<boolean>(false)

    const isQuestionaryCompleted = computed(() => questionsAnswer.value.length > 0 && questionIndex.value === visibleQuestionsAnswer.value.length)
    const isLastQuestion = computed(() => questionsAnswer.value.length > 0 && questionIndex.value === visibleQuestionsAnswer.value.length - 1)

    function processProgress() {
        const questionsSum = visibleQuestionsAnswer.value.filter(q => !q.key.hideQuestion).length;
        progress.value = (questionIndex.value / questionsSum) * 100
    }

    function nextQuestion() {
        if (questionsAnswer.value.length > 0) {
            
            if (currentQuestion?.value === null) {
                currentQuestion.value = questionsAnswer.value[0] ?? null
            } else if(questionsAnswer.value[questionIndex.value + 1] !== undefined){
                currentQuestion.value = questionsAnswer.value[questionIndex.value + 1]
            } else {
                console.warn('###No more questions')
                if(checkError()) return;
            }
        }

        ++questionIndex.value
        processProgress()
    }

    function changeAnswer(questionKey: string, answer: string) {
        const toChange = questionsAnswer.value.filter((q) => q.key.Id === questionKey)[0]

        if (toChange !== undefined && toChange !== null) {
            toChange.value = answer
        }

        if (toChange.key.Type === QuestionType.CategoryQuestion) {
            pushAnswerToUrl(toChange);
        }
    }

    function changeAutomaticTag(questionKey: string, tag: boolean) {
        const toChange = questionsAnswer.value.filter((q) => q.key.Id === questionKey)[0]
        
        if (toChange !== undefined && toChange !== null) {
            toChange.key.IsAutomaticTag = tag
        }
    }

    function pushAnswerToUrl(question: KeyValuePair<InterviewQuestionResponseModel, string>) {
        const location = window.location;
        const [ path ] = location.href.split('?');
        window.history.replaceState(null, '', `${ path }?${queryCategoryKey}=${ question.value.split(';')[0] }` );
    }

    function replaceVariants(category: InterviewQuestionResponseModel, variantToReplace: Variant) {
        const clearVariants = questionsAnswer.value
            .filter(question => question.key.parentCategoryId === category.Id);

        if (!variantToReplace.Script) {
            questionsAnswer.value = clearVariants;

            return;
        }

        const variantsMap = mapQuestions(variantToReplace.Script!)
        
        variantsMap.forEach(q => q.key.parentCategoryId = category.Id)

        const clearVariantsIds = clearVariants.map(v => v.key.Id);
        const draftAnswers = questionsAnswer.value.slice().filter(q => !clearVariantsIds.includes(q.key.Id));
        const startIndex = questionsAnswer.value.findIndex(q => q.key.Id === category.Id);

        draftAnswers.splice(startIndex + 1, 0, ...variantsMap);
        questionsAnswer.value = draftAnswers;
    }

    function resetCategoryIndex(category: InterviewQuestionResponseModel) {
        const startIndex = questionsAnswer.value.findIndex(q => q.key.Id === category.Id);

        // reset current question back to category
        currentQuestion.value = visibleQuestionsAnswer.value[startIndex];
        questionIndex.value = startIndex;
    }

    function checkIfIsTerms() {
        if(getCookie("termsOfUse"))
        {
            isTerms.value = true;
        }
    }

    function checkError()
    {
        if (!(getCookie('termsOfUse') != null && getCookie('privacyPilicy') != null)) {
            questionsAnswerError.value["termsOfUse"] = getLocalization('Accessability.InterviewQuestionary.TermsOfUse')
        }
        if(Object.values(questionsAnswerError.value).every(value => value === null))
        {
            return false;
        }
        const firstErrorInput = document.querySelector(".tw-form-control__field-error--highlight") as HTMLElement
        firstErrorInput?.focus()
        firstErrorInput?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
        })
        return true;
    }

    const progressMasks = computed(() => {
        const answers = questionsAnswer.value.filter((q) => !!q.value);

        return answers.reduce((masks, currentAnswer) => {
            const selectedVariant = currentAnswer.key.Variants?.find(v => v.Value === currentAnswer.value);

            return selectedVariant?.MaskImage ? [ ...masks, selectedVariant.MaskImage! ] : masks;
        }, [] as string[]);
    })

    const visibleQuestionsAnswer = computed(() => questionsAnswer.value.filter(q => !q.key.hideQuestion));
    //#endregion
    //

    return {
        questionIndex,
        questionsAnswer,
        questionsAnswerError,
        visibleQuestionsAnswer,
        progress,
        progressMasks,
        sidebarImages,
        currentQuestion,
        isLastQuestion,
        isQuestionaryCompleted,
        thankYouPage,
        isThanksPageShowing,
        isTermsShowing,
        isWithAdditionalQuestions,
        isTerms,
        userName,
        maxFileSize,
        maxFileSizeMb,
        googleMapKey,
        setUserName,
        checkError,
        replaceVariants,
        resetCategoryIndex,
        fetchQuestions,
        getLocalization,
        submitMedia,
        getPreview,
        nextQuestion,
        changeAnswer,
        changeAutomaticTag,
        postQuestionaryAnswers,
        getCategoryAnswer,
        setCookie,
        getCookie,
        eraseCookie,
        checkIfIsTerms
    }
})
